import { capitalize, statusColor, symbol, formatRate } from '../configUtils';

/*
 * Candidate detail header — fill-in-the-blanks config.
 *
 *   title    : fields joined together to form the heading
 *   eyebrow  : small line shown above the title
 *   status   : the coloured status pill
 *   ids      : id chips shown under the title  → { label, field }
 *   meta     : the icon + text row             → { icon, field }
 *   timestamp: the date line under the header
 *
 * A `field` is the API field name. Use a list like ['a', 'b'] to mean
 * "use a, or b if a is empty". Reorder / relabel freely — that's all it takes.
 */
export const candidateConfig = {
  module: 'candidate',
  tabs: [
    { key: 'details', label: 'Details' },
    { key: 'jobs', label: 'Jobs' },
    { key: 'emails', label: 'Emails' },
    { key: 'attachments', label: 'Attachments' },
  ],
  primaryActions: [
    // Opens the candidate's resume (resolved from the record's file fields) in a new tab.
    { key: 'resume', label: 'Resume', icon: 'resume', tone: 'primary', openDocument: 'resume' },
    // Navigates to the edit screen; ":_id" is filled with the candidate's _id.
    { key: 'edit',   label: 'Edit',   icon: 'edit',   tone: 'primary', route: '/app/candidates/edit/:_id' },
  ],
  moreActions: [
    { key: 'quickSubmit',   label: 'Quick Submit',      route: '/popup/quick-submit' },
    { key: 'addNotesShare', label: 'Add Notes & Share', route: '/popup/add-notes' },
    { key: 'tagToJobs',     label: 'Tag to Jobs',       route: '/popup/tag-to-job' },
    { key: 'addToGroups',   label: 'Add to Groups',     route: '/popup/add-to-groups' },
    { key: 'delete',        label: 'Delete', danger: true, route: '/popup/delete' },
  ],
  header: {
    title: ['firstName', 'middleName', 'lastName'],
    eyebrow: 'source',

    // Use the candidate's status; if there's none but they're placed, say so.
    status: ({ val, has }) => {
      if (has(val('status'))) return { label: capitalize(val('status')), color: statusColor(val('status')) };
      if (val('applicantPlacedStatus') === true) return { label: 'Placed', color: 'success' };
      return null;
    },

    ids: [
      { label: 'Candidate ID', field: ['applicantRefrenceId', 'applicantReferenceId', 'candidateReferenceId'] },
    ],

    meta: [
      { icon: 'designation', field: 'designation' },
      { icon: 'location',    field: ['currentLocation', 'preferredLocation'] },
      { icon: 'experience',  compute: candidateExperience },
      { icon: 'employment',  field: 'workAuthorisation' },
      { icon: 'rate',        prefix: 'Expected: ', compute: expectedRate },
    ],

    timestamp: { label: 'Created Date', field: ['createdAt', 'createdDate'] },
  },
};

// Years of experience, with months appended when present ("5 yrs 6 mo").
function candidateExperience({ val, has }) {
  const years = has(val('yearOfExperience')) ? val('yearOfExperience') : val('experience');
  if (!has(years)) return '';
  const months = val('monthOfExperience');
  const tail = has(months) && Number(months) > 0 ? ` ${months} mo` : '';
  return `${years} yrs${tail}`;
}

// Expected pay, formatted like "₹80/Hour".
function expectedRate({ val }) {
  const pay = val('expectedPayDetails') ?? {};
  return formatRate({
    start: pay.expectedPay ?? val('expectedPay'),
    sym: pay.expectedPayCurrencySymbol ?? symbol(pay.expectedPayCurrency),
    unit: pay.expectedPayUnit,
  });
}

export default candidateConfig;
